import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { ddmmyy, dateToTime } from "../utils/DateUtils";
import AuthContext from "../store/AuthContext";
import EditIcon from "@mui/icons-material/Edit";
import CachedIcon from "@mui/icons-material/Cached";
import AddUserKey from "./AddUserKey";
import ApiEndpoints from "../network/ApiEndPoints";
import EditUserKey from "./EditUserKey";
import { get } from "../network/ApiController";
import CommonMpinModal from "../modals/CommonMpinModal";
import { apiErrorToast } from "../utils/ToastUtil";

const UserKeys = () => {
  const [apiData, setApiData] = useState([]);
  const [query, setQuery] = useState(""); // user_id
  const [openEditKey, setOpenEditKey] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false); // track search click
  const [openMpinModal, setOpenMpinModal] = useState(false);
  const [usersList, setUsersList] = useState([]);

  const authCtx = useContext(AuthContext);
  const user = authCtx?.user;

  const fetchUsersByRole = () => {
    get(
      ApiEndpoints.GET_USERS,
      `page=1&paginate=10&role=Api&export=`,
      null,
      (res) => {
        const data = res?.data?.data;
        const list = Array.isArray(data) ? data : data ? [data] : [];
        setUsersList(list);
      },
      (err) => {
        console.error("Error fetching users:", err);
        setUsersList([]);
      }
    );
  };
  useEffect(() => {
    fetchUsersByRole();
  }, []);

  const handleMpinSuccess = (mpin) => {
    console.log("MPIN Verified:", mpin);

    setOpenMpinModal(false);
    // fetchUserKeys(mpin);

    setLoading(true);
    setSearched(true);

    get(
      ApiEndpoints.GET_USER_KEYS,
      `user_id=${query}&mpin=${mpin}`, // ✅ MPIN sent here
      null,
      (res) => {
        const responseData = res?.data?.data;

        const list = Array.isArray(responseData)
          ? responseData
          : responseData
          ? [responseData]
          : [];

        setApiData(list);
        setLoading(false);
      },
      (err) => {
        apiErrorToast(err);
        console.error("Fetch error", err);
        setApiData([]);
        setLoading(false);
      }
    );
  };

  // =============================
  // RESET
  // =============================
  const handleReset = () => {
    setQuery("");
    setApiData([]);
    setSearched(false);
  };

  return (
    <Box>
      {/* ================= FILTER BAR ================= */}
      <Box mb={5} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          select
          label="Select User"
          size="small"
          value={query}
          onChange={(e) => {
            const selectedUser = e.target.value;
            setQuery(selectedUser);

            // ✅ Clear previous data when a different user is selected
            setApiData([]);
            setSearched(false);
          }}
          sx={{ minWidth: 200 }}
        >
          {usersList.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.id} - {user.name || user.username || user.email}
            </MenuItem>
          ))}
        </TextField>

        {/* 👉 Show buttons only when user types */}
        {query && (
          <>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenMpinModal(true)} // MPIN modal khol do
              disabled={loading}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={handleReset}
            >
              Reset
            </Button>
          </>
        )}

        <AddUserKey />
      </Box>

      <Grid
        container
        spacing={3}
        justifyContent="center" // center horizontally
        alignItems="flex-start"
      >
        {/* 👉 Before search */}
        {!query && !searched && (
          <Grid item xs={12}>
            <Typography
              textAlign="center"
              color="text.secondary"
              variant="subtitle1"
            >
              🔍 Please select User and click Search
            </Typography>
          </Grid>
        )}

        {/* 👉 Loading */}
        {loading && (
          <Grid item xs={12}>
            <Typography textAlign="center" variant="body1">
              Loading...
            </Typography>
          </Grid>
        )}

        {/* 👉 Data Cards */}
        {!loading &&
          apiData?.map((row, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={index}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Box
                sx={{
                  borderRadius: 3,
                  boxShadow: 3,
                  p: 2.5,
                  width: 280, // fixed width for uniform card size
                  backgroundColor: "#fafafa",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Typography
                  fontWeight={700}
                  variant="subtitle1"
                  color="primary"
                  textAlign="center"
                >
                  User ID: {row.user_id}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <b>HKey:</b> {row.hkey}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ wordBreak: "break-all", color: "#555" }}
                >
                  <b>API Token:</b> {row.user?.api_token}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <b>Establishment:</b> {row.user?.establishment || "-"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <b>IP:</b> {row.ip || "-"}
                </Typography>

                <Typography variant="caption" color="text.disabled">
                  {ddmmyy(row.created_at)} • {dateToTime(row.created_at)}
                </Typography>

                <Box mt="auto" textAlign="center">
                  <Tooltip title="Edit User Key">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setEditData(row);
                        setOpenEditKey(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          ))}

        {/* 👉 No Data Found */}
        {searched && !loading && apiData.length === 0 && (
          <Grid item xs={12}>
            <Typography
              textAlign="center"
              color="text.secondary"
              variant="body1"
            >
              ❌ No records found for this User
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* ================= EDIT MODAL ================= */}
      <EditUserKey
        open={openEditKey}
        data={editData}
        handleClose={() => {
          setOpenEditKey(false);
          setEditData(null);
        }}
        onSuccess={() => {
          setOpenEditKey(false);
          setEditData(null);
          handleMpinSuccess();
        }}
      />
      <CommonMpinModal
        open={openMpinModal}
        setOpen={setOpenMpinModal}
        title="Verify MPIN"
        mPinCallBack={handleMpinSuccess} // ✅ MPIN verify ke baad API call
      />
    </Box>
  );
};

export default UserKeys;
